/**
 * PF v2 file hub — the v2 session-file transport.
 * ============================================================================
 *
 * The productCode-keyed replacement for the v1 `order/{sessionId}` mints in
 * `fileHub.transport.ts`. Two resources, eight operations, all under
 * `/workspace/{workspaceId}/sessions/{sessionId}`:
 *
 *   POST   …/files/upload-url            product-scoped mint  → 201 SignedURLDTO
 *   PUT    …/files/upload-url            replace bytes        → 201 UrlDTO
 *   GET    …/files                       listing, by product  → 200 SessionFilesV2DTO
 *   DELETE …/files/{filename}            drops every variant  → 204
 *   GET    …/files/{filename}/download-url                    → 200 UrlDTO
 *   POST   …/order-files/upload-url      order-scoped mint    → 201 SignedURLDTO
 *   GET    …/order-files                 occupied slots       → 200 OrderDocumentsV2DTO
 *   DELETE …/order-files/{type}/{filename}  removes one document → 204
 *
 * Three contract facts that shape the code below:
 *
 * 1. **`kind` is sent on the initial upload only.** Delete, download and re-upload
 *    read it off the session record, because the filename they key on is the
 *    server-assigned name the upload returned. A caller-supplied `kind` that
 *    disagreed with how the file was stored used to delete the record while
 *    deleting nothing in storage.
 * 2. **The workspace in the path is a tenancy check, not decoration.** The backend
 *    binds session→workspace before touching anything and answers 403 on a
 *    mismatch, so the id MUST be the one the session was opened in — which is why
 *    it is read from `draft.sessionWorkspaceId` (stamped by `setSessionId`) rather
 *    than re-derived per call.
 * 3. **Re-upload echoes the stored filename verbatim.** Blobs are named by the
 *    backend (`<sessionId>-backbonephoto-001.pdf`); the client's original name is
 *    kept only in blob metadata, so `gcFilename` is the key — never `fileObject.name`.
 *
 * Requests are issued against the `API` singleton rather than the `useAPI` handle
 * FileAPI passes in, for one hard reason: these endpoints `produce` only the v2
 * role mimetypes, and `useAPI.del`'s options are typed `Omit<…, 'headers'>`, so a
 * delete through it cannot carry a v2 `Accept` and 406s. `resolveVisualDerivative`
 * already reaches for the singleton for the same class of reason.
 */

import API from 'utils/API/API'
import { getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import type { FileDeleteFnc, SignedURLRetrieverFnc } from 'components/common/FileAPI'
import type { SignedURLDTO } from 'models/visuals'
import type {
  OrderDocumentsV2DTO,
  OrderDocumentUploadV2DTO,
  SessionFileUploadV2DTO,
  SessionFilesV2DTO,
  SessionOrderDocumentType
} from 'models/purchaseFlowRevamp'
import { SessionFileKind } from 'models/purchaseFlowRevamp'
import type { UploadTagType } from 'constants/documents'

import type { FileHubBaseMetadata, FileHubContext } from './fileHub.types'

// ─── Endpoints ────────────────────────────────────────────────────────────────

/**
 * The v2 session-file surface. Declared here rather than in `constants/API`
 * alongside the v1 `PurchaseFlowEndpoint` family because these are reached
 * through the `API` singleton (see the file header) and take no part in the
 * `useAPI` endpoint-tagging enum.
 */
export enum SessionFileEndpoint {
  UPLOAD_URL = '/workspace/{workspaceId}/sessions/{sessionId}/files/upload-url',
  REUPLOAD_URL = '/workspace/{workspaceId}/sessions/{sessionId}/files/upload-url',
  LIST = '/workspace/{workspaceId}/sessions/{sessionId}/files',
  DELETE = '/workspace/{workspaceId}/sessions/{sessionId}/files/{filename}',
  DOWNLOAD_URL = '/workspace/{workspaceId}/sessions/{sessionId}/files/{filename}/download-url',
  ORDER_FILE_UPLOAD_URL = '/workspace/{workspaceId}/sessions/{sessionId}/order-files/upload-url',
  ORDER_FILE_LIST = '/workspace/{workspaceId}/sessions/{sessionId}/order-files',
  ORDER_FILE_DELETE = '/workspace/{workspaceId}/sessions/{sessionId}/order-files/{type}/{filename}',
}

// ─── Internals ────────────────────────────────────────────────────────────────

/**
 * Substitutes path placeholders, URI-encoding each value — the same contract
 * `useAPI._getUrl` provides, reimplemented here because the singleton path
 * bypasses `useAPI`. Encoding matters for `{filename}`: server-assigned names are
 * canonical ASCII, but a delete keyed on a legacy or seeded name may not be.
 */
const buildUrl = (endpoint: SessionFileEndpoint, replacements: Record<string, string>): string =>
  Object.entries(replacements).reduce(
    (url, [key, value]) => url.replace(`{${key}}`, encodeURIComponent(value)),
    endpoint as string
  )

/** Accept-only headers, for the verbs that send no body. */
const acceptV2 = async () => ({ Accept: await API.getRoleMimeTypeV2() })

/**
 * Accept + Content-Type, for the verbs that do. Both must be the v2 mimetype:
 * the endpoints `consume` `application/json` as well, but negotiating the
 * response model is what `Accept` decides, so they are set together — the same
 * pairing every other PF v2 call makes.
 */
const jsonV2 = async () => {
  const mimetype = await API.getRoleMimeTypeV2()
  return { Accept: mimetype, 'Content-Type': mimetype }
}

/**
 * Resolves the session identity every operation needs, or explains which half is
 * missing. Both are read live (never closed over) so a late-resolving session
 * never bakes a stale value in, and a missing one rejects cleanly — the chip
 * lands in `FileState.ERROR` instead of the request going out malformed.
 */
const requireSession = (ctx: FileHubContext, operation: string): { workspaceId: string, sessionId: string } => {
  const sessionId = ctx.getSessionId()
  const workspaceId = ctx.getWorkspaceId()
  if (!sessionId) throw new Error(`PF v2 ${operation}: sessionId not ready`)
  // A session always carries the workspace it was opened in, so an absent one
  // means the session itself is not established yet — not that the workspace is
  // optional. Sending the request anyway would 403 on the backend's tenancy gate.
  if (!workspaceId) throw new Error(`PF v2 ${operation}: session workspaceId not ready`)
  return { workspaceId, sessionId }
}

// ─── Product-scoped files ─────────────────────────────────────────────────────

/** What a `/files` mint needs beyond the file itself. `tag` is documents-only. */
export interface SessionFileUploadTarget {
  productCode: string | null | undefined
  kind: SessionFileKind
  tag?: UploadTagType
}

/**
 * Builds the FileAPI upload signed-URL retriever for a file attached to one
 * product line — the single replacement for both v1 mints (`visual/uploadUrl` and
 * `document/uploadUrl`), which differ here only by `kind`.
 *
 * Sequential (via `getSignedURLRetrieverSeq`) for the same reason as v1: GCS can
 * mint duplicate filenames if signed-URL requests fire too fast.
 *
 * `productCode` is passed through EXACTLY as the catalogue emitted it. There is no
 * parse and no derived id — that is the whole point of this endpoint. A missing
 * code rejects cleanly rather than sending a blank one for the backend to reject.
 */
export function makeSessionFileUploadResolver<Metadata extends FileHubBaseMetadata>(
  ctx: FileHubContext,
  target: SessionFileUploadTarget
): SignedURLRetrieverFnc<Metadata> {
  return getSignedURLRetrieverSeq<Metadata>(async (fileEntry) => {
    const { workspaceId, sessionId } = requireSession(ctx, 'file upload')
    const { productCode, kind, tag } = target
    if (!productCode) throw new Error('PF v2 file upload: no product code for the selected variant')

    const body: SessionFileUploadV2DTO = {
      productCode,
      kind,
      filename: fileEntry.fileObject.name,
      contentType: fileEntry.fileObject.type,
      // Rejected outright for a VISUAL, so it is omitted rather than sent as
      // undefined-and-hopefully-ignored.
      ...(kind === SessionFileKind.DOCUMENT && tag ? { tag } : {}),
    }

    return API.post<SignedURLDTO>(
      buildUrl(SessionFileEndpoint.UPLOAD_URL, { workspaceId, sessionId }),
      body,
      { headers: await jsonV2() },
      { endpoint: SessionFileEndpoint.UPLOAD_URL }
    )
  })
}

/**
 * Builds the FileAPI delete handler for a product-scoped file.
 *
 * One handler for visuals and documents alike: the backend reads the kind off the
 * session record and removes the whole variant set (a visual fans out into
 * RAW / RAW_THUMB / RAW_WEB), so there is nothing kind-shaped to pass. Keys off
 * `gcFilename ?? id` — the server-assigned name — exactly as the v1 handlers did.
 */
export function makeSessionFileDeleteHandler<Metadata extends FileHubBaseMetadata>(ctx: FileHubContext): FileDeleteFnc<Metadata> {
  return async (fileEntry) => {
    const { workspaceId, sessionId } = requireSession(ctx, 'file delete')
    const filename = fileEntry.gcFilename ?? fileEntry.id

    return API.delete<void>(
      buildUrl(SessionFileEndpoint.DELETE, { workspaceId, sessionId, filename }),
      { headers: await acceptV2() },
      { endpoint: SessionFileEndpoint.DELETE }
    )
  }
}

/**
 * Lists every file uploaded against a product line, keyed by product code.
 *
 * This is the read side the register has wanted since E-2 / DF-15 / DF-24: there
 * was no document **listing** endpoint, so a resumed draft could not re-show its
 * uploaded files and had to fall back to FE-only filename markers. Returns
 * visuals and documents together — one call, not two to merge.
 */
export async function listSessionFiles(ctx: FileHubContext): Promise<SessionFilesV2DTO> {
  const { workspaceId, sessionId } = requireSession(ctx, 'file listing')

  const response = await API.get<SessionFilesV2DTO>(
    buildUrl(SessionFileEndpoint.LIST, { workspaceId, sessionId }),
    { headers: await acceptV2() },
    { endpoint: SessionFileEndpoint.LIST }
  )
  return response.data ?? { files: {} }
}

// ─── Order-scoped documents ───────────────────────────────────────────────────

/**
 * Builds the FileAPI upload signed-URL retriever for a document belonging to the
 * order as a whole — Power of Attorney, its representatives sheet, and
 * supplementary files. Carries no product reference at all.
 *
 * This replaces two different v1 shapes at once: the dedicated PoA mints
 * (`power-of-attorney[-representatives]/uploadUrl`, body `{filename, contentType}`)
 * and the supplementary-document case, which v1 routed through the *product*
 * endpoint by overloading the `ORDER_SUPPLEMENTARY_DOCUMENT` tag — a marker that
 * silently decided which entity the file attached to. v2 makes the distinction an
 * endpoint, and rejects that tag on `/files`.
 *
 * Cardinality is per slot, and it is the slot's **declared** cardinality that
 * matters, not how many rows happen to be present: `POWER_OF_ATTORNEY` and
 * `POWER_OF_ATTORNEY_REPRESENTATIVES` hold one document each and reject a second
 * upload with 409 until the first is deleted, while `SUPPLEMENTARY` holds several,
 * so uploading more is expected. That matches v1 on all three.
 */
export function makeOrderDocumentUploadResolver<Metadata extends FileHubBaseMetadata>(
  ctx: FileHubContext,
  type: SessionOrderDocumentType
): SignedURLRetrieverFnc<Metadata> {
  return getSignedURLRetrieverSeq<Metadata>(async (fileEntry) => {
    const { workspaceId, sessionId } = requireSession(ctx, 'order-document upload')

    const body: OrderDocumentUploadV2DTO = {
      type,
      filename: fileEntry.fileObject.name,
      contentType: fileEntry.fileObject.type,
    }

    return API.post<SignedURLDTO>(
      buildUrl(SessionFileEndpoint.ORDER_FILE_UPLOAD_URL, { workspaceId, sessionId }),
      body,
      { headers: await jsonV2() },
      { endpoint: SessionFileEndpoint.ORDER_FILE_UPLOAD_URL }
    )
  })
}

/** Lists the order-level documents the session holds, one row per occupied slot document. */
export async function listOrderDocuments(ctx: FileHubContext): Promise<OrderDocumentsV2DTO> {
  const { workspaceId, sessionId } = requireSession(ctx, 'order-document listing')

  const response = await API.get<OrderDocumentsV2DTO>(
    buildUrl(SessionFileEndpoint.ORDER_FILE_LIST, { workspaceId, sessionId }),
    { headers: await acceptV2() },
    { endpoint: SessionFileEndpoint.ORDER_FILE_LIST }
  )
  return response.data ?? { documents: [] }
}

/**
 * Builds the FileAPI delete handler for an order-level document.
 *
 * Addresses the document by **slot + filename** for all three slots, not by slot
 * alone. The backend documents the filename form as equivalent to addressing the
 * slot for the single-document slots and *required* for `SUPPLEMENTARY`, which holds
 * several — so one uniform call is correct everywhere and there is no per-slot
 * branch to get wrong. Addressing `SUPPLEMENTARY` by slot alone is refused with 409
 * precisely so a stale caller cannot delete a document it never named.
 *
 * E-1 stays closed by construction, as with the product-file delete: the filename
 * comes from the entry (`gcFilename ?? id`), so a placeholder can never survive
 * unsubstituted.
 */
export function makeOrderDocumentDeleteHandler<Metadata extends FileHubBaseMetadata>(
  ctx: FileHubContext,
  type: SessionOrderDocumentType
): FileDeleteFnc<Metadata> {
  return async (fileEntry) => {
    const { workspaceId, sessionId } = requireSession(ctx, 'order-document delete')
    const filename = fileEntry.gcFilename ?? fileEntry.id

    return API.delete<void>(
      buildUrl(SessionFileEndpoint.ORDER_FILE_DELETE, { workspaceId, sessionId, type, filename }),
      { headers: await acceptV2() },
      { endpoint: SessionFileEndpoint.ORDER_FILE_DELETE }
    )
  }
}

/**
 * Deletes a session document the backend reported as no longer stored, so its record stops refusing
 * the placement. An already-absent file counts as deleted on the backend, so this cannot fail on
 * the very condition it clears. Order documents are addressed by slot, product documents by name.
 */
export async function deleteMissingSessionDocument(
  ctx: FileHubContext,
  file: { filename: string, orderDocumentType: string | null }
): Promise<void> {
  const { workspaceId, sessionId } = requireSession(ctx, 'missing document delete')
  const url = file.orderDocumentType
    ? buildUrl(SessionFileEndpoint.ORDER_FILE_DELETE, { workspaceId, sessionId, type: file.orderDocumentType, filename: file.filename })
    : buildUrl(SessionFileEndpoint.DELETE, { workspaceId, sessionId, filename: file.filename })
  const endpoint = file.orderDocumentType ? SessionFileEndpoint.ORDER_FILE_DELETE : SessionFileEndpoint.DELETE
  await API.delete<void>(url, { headers: await acceptV2() }, { endpoint })
}
